import React from 'react';
import { Check, Loader2, Plus, X } from 'lucide-react';

import {
  NEXUS_EXPENSE_TYPES,
  NEXUS_RATER_TIERS,
  type NexusBugCostReport,
  type NexusBugEstimateReport,
  type NexusBugMetadata,
  type NexusBugReportResult,
} from '../../../../domain/nexus/model';
import { useBugCostViewModel, useBugEstimateViewModel } from './bugsViewModels';
import { MAX_REPORT_LINES, costLinesTotalCents, formatCents, formatSeconds, rateLabel } from './bugReportHelpers';
import { NexusErrorBanner } from './ErrorBanner';
import { formatNexusTimestamp } from './helpers';

const CARD = 'space-y-4 rounded-md border border-gray-200 bg-white p-4 sm:p-5';
const PRIMARY_BUTTON = 'inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50';
const ADD_BUTTON = 'inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline disabled:opacity-50';

/** Send this bug's estimate or a period's actual cost to Google. */
export function BugReportsSection({ bug, onSent }: { bug: NexusBugMetadata; onSent: (result: NexusBugReportResult) => void }) {
  return (
    <section aria-labelledby="nexus-bug-reports" className="mt-6 max-w-5xl space-y-4">
      <div>
        <h3 id="nexus-bug-reports" className="text-base font-semibold text-gray-900">Report to Google</h3>
        <p className="mt-0.5 text-sm text-gray-500">
          Send an estimate as soon as you have one, and again if it changes significantly. Report each period&apos;s actual
          cost within 24–72 hours of the period ending, once the data is accepted.
        </p>
      </div>
      <EstimateCard bugId={bug.deliveryBugId} last={bug.lastEstimateReport ?? null} onSent={onSent} />
      <CostCard bugId={bug.deliveryBugId} last={bug.lastCostReport ?? null} onSent={onSent} />
    </section>
  );
}

// --- Estimate -------------------------------------------------------------------

function EstimateCard({ bugId, last, onSent }: { bugId: string; last: NexusBugEstimateReport | null; onSent: (result: NexusBugReportResult) => void }) {
  const vm = useBugEstimateViewModel(bugId, onSent);
  return (
    <form
      aria-label="Estimate"
      className={`${CARD} max-w-2xl`}
      onSubmit={(event) => {
        event.preventDefault();
        void vm.send((message) => window.confirm(message));
      }}
    >
      <CardHeader title="Estimate" description="What the whole study is expected to cost. Sent to Google as estimates." />
      {vm.error && <NexusErrorBanner message={vm.error} onDismiss={vm.clearError} />}
      <div className="grid gap-3 sm:grid-cols-3">
        <TextInput id="nexus-estimate-cost" label="Estimated total cost (USD)" inputMode="decimal" placeholder="e.g. 50000"
          value={vm.form.costUsd} error={vm.errors.costUsd} onChange={(value) => vm.setField('costUsd', value)} />
        <TextInput id="nexus-estimate-aht" label="Average handling time (min, optional)" inputMode="decimal" placeholder="e.g. 5"
          value={vm.form.ahtMinutes} error={vm.errors.ahtMinutes} onChange={(value) => vm.setField('ahtMinutes', value)} />
        <TextInput id="nexus-estimate-time" label="Estimate time" type="datetime-local"
          value={vm.form.reportTime} error={vm.errors.reportTime} onChange={(value) => vm.setField('reportTime', value)} />
      </div>
      <SendRow label="Send estimate" sending={vm.sending} sent={vm.sent} />
      {last ? (
        <LastSent report={last} rows={[
          ['Estimated cost', formatCents(last.costCents)],
          ['Handling time', formatSeconds(last.ahtSeconds)],
          ['Estimate time', formatNexusTimestamp(last.reportTime)],
        ]} />
      ) : <NothingSent />}
    </form>
  );
}

// --- Actual cost ----------------------------------------------------------------

function CostCard({ bugId, last, onSent }: { bugId: string; last: NexusBugCostReport | null; onSent: (result: NexusBugReportResult) => void }) {
  const vm = useBugCostViewModel(bugId, onSent);
  const linesTotal = costLinesTotalCents(vm.form);
  return (
    <form
      aria-label="Actual cost"
      className={CARD}
      onSubmit={(event) => {
        event.preventDefault();
        void vm.send((message) => window.confirm(message));
      }}
    >
      <CardHeader
        title="Actual cost"
        description="What one reporting period cost, broken down into rater and auxiliary costs. Sent to Google as metrics."
      />
      {vm.error && <NexusErrorBanner message={vm.error} onDismiss={vm.clearError} />}
      <div className="grid gap-3 sm:grid-cols-3">
        <TextInput id="nexus-cost-time" label="Report time" type="datetime-local"
          value={vm.form.reportTime} error={vm.errors.reportTime} onChange={(value) => vm.setField('reportTime', value)} />
        <TextInput id="nexus-cost-total" label="Total cost, full price (USD)" inputMode="decimal" placeholder="e.g. 1500.00"
          value={vm.form.costUsd} error={vm.errors.costUsd} onChange={(value) => vm.setField('costUsd', value)}
          help={linesTotal ? `Lines below add up to ${formatCents(linesTotal)}.` : undefined} />
        <TextInput id="nexus-cost-discounted" label="Discounted price (USD, optional)" inputMode="decimal" placeholder="e.g. 1400.00"
          value={vm.form.discountedUsd} error={vm.errors.discountedUsd} onChange={(value) => vm.setField('discountedUsd', value)} />
      </div>

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-gray-900">Rater costs</legend>
        <p className="text-xs text-gray-500">One line per rater tier and locale.</p>
        {vm.form.raterCosts.map((line, index) => {
          const errors = vm.errors.raterCosts[index] || {};
          const id = (field: string) => `nexus-rater-${line.key}-${field}`;
          return (
            <LineBox key={line.key} label={`Rater cost ${index + 1}`} onRemove={() => vm.removeLine('raterCosts', index)}>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <SelectInput id={id('tier')} label="Rater tier" value={line.raterTier} options={NEXUS_RATER_TIERS}
                  error={errors.raterTier} onChange={(value) => vm.setRaterField(index, 'raterTier', value)} />
                <TextInput id={id('locale')} label="Locale" placeholder="en-us"
                  value={line.locale} error={errors.locale} onChange={(value) => vm.setRaterField(index, 'locale', value)} />
                <SelectInput id={id('expense')} label="Expense type" value={line.expenseType} options={NEXUS_EXPENSE_TYPES}
                  error={errors.expenseType} onChange={(value) => vm.setRaterField(index, 'expenseType', value)} />
                <TextInput id={id('aht')} label="Average handling time (min)" inputMode="decimal" placeholder="e.g. 4"
                  value={line.ahtMinutes} error={errors.ahtMinutes} onChange={(value) => vm.setRaterField(index, 'ahtMinutes', value)} />
                <TextInput id={id('minutes')} label="Total minutes worked" inputMode="decimal" placeholder="e.g. 1200.5"
                  value={line.totalMinutes} error={errors.totalMinutes} onChange={(value) => vm.setRaterField(index, 'totalMinutes', value)} />
                <TextInput id={id('volume')} label="Total volume (incl. replications)" inputMode="numeric" placeholder="e.g. 300"
                  value={line.totalVolume} error={errors.totalVolume} onChange={(value) => vm.setRaterField(index, 'totalVolume', value)} />
                <TextInput id={id('rate')} label={rateLabel(line.expenseType)} inputMode="decimal" placeholder="e.g. 35.00"
                  value={line.rateUsd} error={errors.rateUsd} onChange={(value) => vm.setRaterField(index, 'rateUsd', value)} />
                <TextInput id={id('cost')} label="Cost (USD)" inputMode="decimal" placeholder="e.g. 700.00"
                  value={line.costUsd} error={errors.costUsd} onChange={(value) => vm.setRaterField(index, 'costUsd', value)} />
              </div>
            </LineBox>
          );
        })}
        <button type="button" className={ADD_BUTTON} disabled={vm.form.raterCosts.length >= MAX_REPORT_LINES} onClick={vm.addRaterCost}>
          <Plus className="h-4 w-4" /> Add rater cost
        </button>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-semibold text-gray-900">Auxiliary costs</legend>
        <p className="text-xs text-gray-500">Costs other than raters, such as tokens.</p>
        {vm.form.auxiliaryCosts.map((line, index) => {
          const errors = vm.errors.auxiliaryCosts[index] || {};
          const id = (field: string) => `nexus-auxiliary-${line.key}-${field}`;
          return (
            <LineBox key={line.key} label={`Auxiliary cost ${index + 1}`} onRemove={() => vm.removeLine('auxiliaryCosts', index)}>
              <div className="grid gap-3 sm:grid-cols-[1fr,2fr,1fr]">
                <TextInput id={id('type')} label="Cost type" placeholder="e.g. Token Cost"
                  value={line.costType} error={errors.costType} onChange={(value) => vm.setAuxiliaryField(index, 'costType', value)} />
                <TextInput id={id('description')} label="Description (optional)" placeholder="e.g. API usage tokens for verification"
                  value={line.description} error={errors.description} onChange={(value) => vm.setAuxiliaryField(index, 'description', value)} />
                <TextInput id={id('cost')} label="Cost (USD)" inputMode="decimal" placeholder="e.g. 100.00"
                  value={line.costUsd} error={errors.costUsd} onChange={(value) => vm.setAuxiliaryField(index, 'costUsd', value)} />
              </div>
            </LineBox>
          );
        })}
        <button type="button" className={ADD_BUTTON} disabled={vm.form.auxiliaryCosts.length >= MAX_REPORT_LINES} onClick={vm.addAuxiliaryCost}>
          <Plus className="h-4 w-4" /> Add auxiliary cost
        </button>
      </fieldset>

      {vm.errors.lines && <p role="alert" className="text-sm text-red-700">{vm.errors.lines}</p>}
      <SendRow label="Send cost" sending={vm.sending} sent={vm.sent} />
      {last ? (
        <LastSent report={last} rows={[
          ['Total cost', formatCents(last.costCents)],
          ['Discounted', last.discountedPriceCents === null ? '—' : formatCents(last.discountedPriceCents)],
          ['Lines', `${last.raterCosts.length} rater · ${last.auxiliaryCosts.length} auxiliary`],
          ['Report time', formatNexusTimestamp(last.reportTime)],
        ]} />
      ) : <NothingSent />}
    </form>
  );
}

// --- Pieces -------------------------------------------------------------------

function CardHeader({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      <p className="mt-0.5 text-xs text-gray-500">{description}</p>
    </div>
  );
}

function LineBox({ label, onRemove, children }: { label: string; onRemove: () => void; children: React.ReactNode }) {
  return (
    <div role="group" aria-label={label} className="rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <button type="button" onClick={onRemove} aria-label={`Remove ${label.toLowerCase()}`}
          className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700">
          <X className="h-4 w-4" />
        </button>
      </div>
      {children}
    </div>
  );
}

function SendRow({ label, sending, sent }: { label: string; sending: boolean; sent: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-3 pt-1">
      <button type="submit" disabled={sending} className={PRIMARY_BUTTON}>
        {sending && <Loader2 className="h-4 w-4 animate-spin" />}
        {label}
      </button>
      {sent && (
        <span role="status" className="inline-flex items-center gap-1 text-sm text-emerald-700">
          <Check className="h-4 w-4" /> Sent
        </span>
      )}
    </div>
  );
}

function NothingSent() {
  return <p className="text-xs text-gray-500">Nothing sent yet.</p>;
}

function LastSent({ report, rows }: { report: NexusBugCostReport | NexusBugEstimateReport; rows: [string, string][] }) {
  return (
    <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 border-t border-gray-100 pt-3 text-xs">
      <dt className="text-gray-500">Last sent</dt>
      <dd className="text-gray-900">
        {formatNexusTimestamp(report.reportedAt)}
        {report.apiEnvironment === 'staging' && <span className="ml-1 text-amber-700">(staging)</span>}
      </dd>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt className="text-gray-500">{label}</dt>
          <dd className="text-gray-900">{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

const INPUT_CLASS = 'mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1';

function inputTone(error?: string): string {
  return error ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary focus:ring-primary';
}

function FieldMessage({ id, error, help }: { id: string; error?: string; help?: string }) {
  if (error) return <p id={id} className="mt-1 text-xs text-red-700">{error}</p>;
  if (help) return <p id={id} className="mt-1 text-xs text-gray-500">{help}</p>;
  return null;
}

interface TextInputProps {
  id: string;
  label: string;
  value: string;
  error?: string;
  help?: string;
  type?: 'text' | 'datetime-local';
  inputMode?: 'decimal' | 'numeric';
  placeholder?: string;
  onChange: (value: string) => void;
}

function TextInput({ id, label, value, error, help, type = 'text', inputMode, placeholder, onChange }: TextInputProps) {
  const messageId = `${id}-message`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
      <input
        id={id}
        type={type}
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error || help ? messageId : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={`${INPUT_CLASS} ${inputTone(error)}`}
      />
      <FieldMessage id={messageId} error={error} help={help} />
    </div>
  );
}

interface SelectInputProps {
  id: string;
  label: string;
  value: string;
  options: readonly string[];
  error?: string;
  onChange: (value: string) => void;
}

function SelectInput({ id, label, value, options, error, onChange }: SelectInputProps) {
  const messageId = `${id}-message`;
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
      <select
        id={id}
        value={value}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? messageId : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={`${INPUT_CLASS} ${inputTone(error)}`}
      >
        <option value="">Choose…</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
      <FieldMessage id={messageId} error={error} />
    </div>
  );
}
